import { useState, useEffect } from 'react';
import { getAnalyticsEvents } from '../../services/analyticsService';
import { getCollection } from '../../services/firestoreService';

const CostsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [costEstimates, setCostEstimates] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Get analytics events to estimate API usage
        const events = await getAnalyticsEvents(30);
        
        // Get collections to estimate Firestore reads/writes
        const users = await getCollection('users', undefined, undefined, undefined, 1).catch(() => []);
        const subscriptions = await getCollection('user_subscriptions', undefined, undefined, undefined, 1).catch(() => []);

        // Estimate costs (rough calculations based on typical Firebase pricing)
        // Firestore: $0.06 per 100k document reads, $0.18 per 100k writes
        // Analytics events would generate reads
        const estimatedReads = events.length; // Each event fetch = read
        const estimatedReadsCost = (estimatedReads / 100000) * 0.06;

        // Estimate writes (assume 10% of events result in writes)
        const estimatedWrites = Math.floor(events.length * 0.1);
        const estimatedWritesCost = (estimatedWrites / 100000) * 0.18;

        // Storage estimate (very rough)
        const estimatedStorageMB = 100; // Placeholder
        const estimatedStorageCost = (estimatedStorageMB / 1024) * 0.18; // $0.18 per GB

        // OpenAI API cost estimation from tracked AI generation events
        const aiGenerationEvents = events.filter(
          (e) => e.eventName === 'ai_content_generation' || e.eventName.includes('ai_generation') || e.eventName.includes('content_generation')
        );
        const assistantEvents = events.filter((e) => e.eventName.includes('assistant')).length;
        const estimatedAPICalls = aiGenerationEvents.length || (assistantEvents * 2);
        // GPT-4o-mini: ~$0.00015/1K input tokens + ~$0.0006/1K output tokens; avg ~$0.002/call
        const estimatedAPICost = estimatedAPICalls * 0.002;

        // Total estimated monthly cost
        const totalEstimatedCost = estimatedReadsCost + estimatedWritesCost + estimatedStorageCost + estimatedAPICost;

        setCostEstimates({
          estimatedReads,
          estimatedWrites,
          estimatedStorageMB,
          estimatedAPICalls,
          estimatedReadsCost,
          estimatedWritesCost,
          estimatedStorageCost,
          estimatedAPICost,
          totalEstimatedCost,
          eventsCount: events.length,
          assistantUsage: assistantEvents,
        });
      } catch (error) {
        console.error('Error loading cost data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading cost estimates...</p>
      </div>
    );
  }

  if (!costEstimates) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No cost data available</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Estimated Cost Tracking
      </h1>

      {/* Total Cost KPI */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '2rem',
        borderLeft: '4px solid #f59e0b',
      }}>
        <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Estimated Monthly Cost</p>
        <h2 style={{ margin: '0.5rem 0', fontSize: '2.5rem', fontWeight: 'bold' }}>
          ${costEstimates.totalEstimatedCost.toFixed(2)}
        </h2>
        <p style={{ margin: 0, color: '#999', fontSize: '0.75rem' }}>
          Estimated from last 30 days event data (not real billing)
        </p>
      </div>

      {/* Cost Breakdown */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Firestore Reads</p>
          <h3 style={{ margin: '0.5rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
            {costEstimates.estimatedReads.toLocaleString()}
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.875rem' }}>
            ${costEstimates.estimatedReadsCost.toFixed(4)}
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Firestore Writes</p>
          <h3 style={{ margin: '0.5rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
            {costEstimates.estimatedWrites.toLocaleString()}
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.875rem' }}>
            ${costEstimates.estimatedWritesCost.toFixed(4)}
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Storage</p>
          <h3 style={{ margin: '0.5rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
            {costEstimates.estimatedStorageMB} MB
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.875rem' }}>
            ${costEstimates.estimatedStorageCost.toFixed(4)}
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>API Calls</p>
          <h3 style={{ margin: '0.5rem 0', fontSize: '1.5rem', fontWeight: 'bold' }}>
            {costEstimates.estimatedAPICalls.toLocaleString()}
          </h3>
          <p style={{ margin: 0, color: '#999', fontSize: '0.875rem' }}>
            ${costEstimates.estimatedAPICost.toFixed(4)}
          </p>
        </div>
      </div>

      {/* Usage Metrics */}
      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>Usage Metrics (Last 30 Days)</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Analytics Events</p>
            <p style={{ margin: '0.5rem 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
              {costEstimates.eventsCount.toLocaleString()}
            </p>
          </div>
          <div>
            <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Assistant Interactions</p>
            <p style={{ margin: '0.5rem 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
              {costEstimates.assistantUsage.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* Cost Optimization Tips */}
      <div style={{
        backgroundColor: '#fef3c7',
        padding: '1.5rem',
        borderRadius: '8px',
        marginTop: '2rem',
        borderLeft: '4px solid #f59e0b',
      }}>
        <h3 style={{ marginTop: 0, color: '#92400e' }}>💡 Cost Optimization Tips</h3>
        <ul style={{ color: '#78350f', margin: '0.5rem 0', paddingLeft: '1.5rem' }}>
          <li>Implement caching for frequently accessed data to reduce Firestore reads</li>
          <li>Batch write operations to minimize Firestore write costs</li>
          <li>Use pagination and limits when querying large collections</li>
          <li>Archive old analytics events to reduce storage costs</li>
          <li>Monitor API usage and optimize assistant interactions</li>
        </ul>
      </div>
    </div>
  );
};

export default CostsTab;

